import { create } from 'zustand';
import i18n from 'i18next'
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import dayjs from "dayjs";

import { apiDomain, basename } from '../setting';
import useModalStore from './useModalStore';
import useAuthStore from './useAuthStore';
import useLoadingStore from './useLoadingStore';
import useAuthAdminStore from './useAuthAdminStore';
import withErrorHandling from '../function/withErrorHandling';
import withErrorHandlingForAll from '../function/withErrorHandlingForAll';
import { ContentComposeEmail } from "../components/admin/application/Email";
import { ContentApprove1, ContentApprove2, ContentApprove3, ContentSMMBenefit, ContentBasicHospitalAndSurgicalBenefits, ContentClaimRequest, ContentScheduledChargeback,ContentDecline1 } from "../components/admin/application/ApplicationInfo";
import FormText from "../components/form/FormText";
import Flex from "../components/core/Flex";

const useAdminApplicationDetailStore = create((set, get) => ({
    data: {},
    isLoadingAdmGetPreAuthForm:false,
    admGetPreAuthForm: async (aid) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        set({
            isLoadingAdmGetPreAuthForm:true
        })
        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admGetPreAuthForm?aid=${aid}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();
        set({
            data: data,
            detailRedPoint: data.form.notifyadm,
            documentRedPoint: data.suppattachmentRedPoint,
            suppattachmentForRedPoint: data.suppattachment,
            intstatus: { label: data.form.intstatusdesc, value: data.form.intstatus, color: "var(--aia-bx-dark-blue)", bold: true },
            requestReleaseLimit:{    
                requestReleaseCardCreateDate:data.form.requestReleaseCardCreateDate,
                requestReleaseCardUpdateDate:data.form.requestReleaseCardUpdateDate,
                releaseCardLimitDate:data.form.releaseCardLimitDate,
                actullyReleaseCardLimitDate:data.form.actullyReleaseCardLimitDate
            },
            isLoadingAdmGetPreAuthForm:false
        });
    },

    admUpdatePreAuthForm: async (aid, data) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admUpdatePreAuthForm`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "aid": aid,
                ...data,
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }
    },

    emailTemplate: {},
    openEmailTemplate: async (aid, template) => {
        if (template === "") {
            set({
                emailTemplate: {},
                contentComposeEmail_Template: {
                    label: "Please Select Email Template",
                    value: ""
                },
                contentComposeEmail_Title: "",
                contentComposeEmail_Content: "",
                emailAttachment: [],
                isValidEmailTitle: true,
                isValidEmailTemplate: true,
            });
            return;
        }
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { contentComposeEmail_Reason } = get();

        /**
         * email template使用
         */
        const finalReasonString = template === "DECLINEDLOG" || template === "DECLINEDPCA" || template === "CHGPCA" ? `&finalReason=${contentComposeEmail_Reason.join(",")}` : "";

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/openEmailTemplate?aid=${aid}&templateNo=${template}${finalReasonString}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        set({
            emailTemplate: data,
            contentComposeEmail_Template: {
                label: data?.email?.template,
                value: data?.email?.templateno,
            },
            contentComposeEmail_Title: data?.email?.subject,
            contentComposeEmail_Content: data?.email?.body,
            emailAttachment: [],
            isValidEmailTitle: true,
        });
    },
    editorState: EditorState.createEmpty(),
    setEditorState: (value) => set((state) => {
        return { editorState: value }
    }),
    admSendEmail: async (aid) => {
        const { contentComposeEmail_Title, editorState, emailTemplate } = get();
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { contentComposeEmail_Reason } = get();

        const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        /**
         * sendEmail接口
         */
        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admSendEmail`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "aid": aid,
                "subject": contentComposeEmail_Title,
                "body": html,
                "templateno": emailTemplate?.email?.templateno,
                "emailId": emailTemplate?.email?.emailId,
                "finalReason": (emailTemplate?.email?.templateno === "DECLINEDLOG" || emailTemplate?.email?.templateno === "DECLINEDPCA" || emailTemplate?.email?.templateno === "CHGPCA") ? contentComposeEmail_Reason.join(",") : "",
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        set({
            emailTemplate: {},
            emailAttachment: [],
            contentComposeEmail_Template: {
                "label": "Please Select Email Template",
                "value": ""
            },
            contentComposeEmail_Reason: [],
            contentComposeEmail_Title: "",
            contentComposeEmail_Content: "",
            isValidEmailTemplate: true,
            isValidEmailTitle: true,
        });

        return data;
    },

    emailAttachment: [],
    addEmailAttachment: (file) => set((state) => {
        return { emailAttachment: [...state.emailAttachment, file] }
    }),
    removeEmailAttachment: async (attachobjid) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/general/delete/${attachobjid}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        // const data = await response.json();

        set((state) => {
            const updatedAttachment = state.emailAttachment.filter((attachment) => attachment.attachobjid !== attachobjid);
            return { emailAttachment: updatedAttachment };
        });

    },

    admChgPreAuthFormStatus: async (aid, intstatus) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admChgPreAuthFormStatus`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                "aid": aid,
                "intstatus": intstatus.value
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        set({
            intstatus: { label: intstatus.label, value: intstatus.value, color: "var(--aia-bx-dark-blue)", bold: true },
        });

        return data;
    },

    selectedTab: 'status',
    setSelectedTab: (value) => set((state) => {
        return { selectedTab: value }
    }),

    isTaskListOpen: true,
    setIsTaskListOpen: (value) => set((state) => {
        return { isTaskListOpen: value }
    }),

    intstatusList: [
        { label: "New Requests", value: "NEW", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Pending Supple", value: "SUPPLE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Pending Review", value: "REVIEW", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Approved (LOG)", value: "APPROVELOG", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Approved (PCA)", value: "APPROVEPCA", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "LOG > PCA", value: "CHGTOPCA", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "PCA > LOG", value: "CHGTOLOG", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Declined", value: "DECLINED", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Pending Chargeback", value: "PENDCHARGE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Scheduled Chargeback", value: "SCHCHARGE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Chargeback Settled", value: "CHARGESETTLE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Outstanding Chargeback", value: "OUTCHARGE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Claim Settled", value: "CLAIMSETTLE", color: "var(--aia-bx-dark-blue)", bold: true },
        { label: "Cancelled", value: "CANCELLED", color: "var(--aia-bx-dark-blue)", bold: true },
    ],
    intstatus: {},

    //Treatment Type
    contentApprove1_TreatmentType_List: [
        {
            "value": "",
            "label": "Please Select"
        },
        {
            "value": "Clinical Surgery/ Daycase / MOP",
            "label": "Clinical Surgery/ Daycase / MOP"
        },
        {
            "value": "IP without Surgery",
            "label": "IP without Surgery"
        },
        {
            "value": "IP with surgery",
            "label": "IP with surgery"
        },
        {
            "value": "Advanced Imanging",
            "label": "Advanced Imanging"
        },
        {
            "value": "OP investigation / TX",
            "label": "OP investigation / TX"
        }
    ],
    contentApprove1_TreatmentType: {
        "value": "",
        "label": "Please Select"
    },
    setContentApprove1_TreatmentType: (value) => set((state) => {
        return { contentApprove1_TreatmentType: value }
    }),
    isValid_contentApprove1_TreatmentType: true,
    setIsValid_contentApprove1_TreatmentType: (value) => set((state) => {
        return { isValid_contentApprove1_TreatmentType: value }
    }),

    //Classification of Surgery
    contentApprove1_ClassificationOfSurgery_List: [
        {
            "value": "",
            "label": "Please Select"
        },
        {
            "value": "Minor",
            "label": "Minor"
        },
        {
            "value": "Inter",
            "label": "Inter"
        },
        {
            "value": "Major",
            "label": "Major"
        },
        {
            "value": "Complex",
            "label": "Complex"
        },
        {
            "value": "NA",
            "label": "NA"
        }
    ],
    contentApprove1_ClassificationOfSurgery: {
        "value": "",
        "label": "Please Select"
    },
    setContentApprove1_ClassificationOfSurgery: (value) => set((state) => {
        return { contentApprove1_ClassificationOfSurgery: value }
    }),
    isValid_contentApprove1_ClassificationOfSurgery: true,
    setIsValid_contentApprove1_ClassificationOfSurgery: (value) => set((state) => {
        return { isValid_contentApprove1_ClassificationOfSurgery: value }
    }),

    //Provide ICD Code - Old (free text)
    contentApprove1_ICD: "",
    setContentApprove1_ICD: (value) => set((state) => {
        return { contentApprove1_ICD: value }
    }),

    //Provide ICD Code - New (ICD code)
    contentApprove1_ICD_code: [],
    setContentApprove1_ICD_code: (value) => set((state) => {
        return { contentApprove1_ICD_code: value }
    }),

    //Provide ICD Code - New (ICD code)
    contentApprove1_ICD_code_list: [],
    setContentApprove1_ICD_code_list: (value) => set((state) => {
        return { contentApprove1_ICD_code_list: value }
    }),

    //Remarks
    contentApprove1_Remarks: [],
    setContentApprove1_Remarks: (value) => set((state) => {
        return { contentApprove1_Remarks: value }
    }),

    contentApprove1_Remarks_text: "",
    setContentApprove1_RemarksText: (value) => set((state) => {
        return { contentApprove1_Remarks_text: value }
    }),

    contentApprove1_savingAmount: "",
    setContentApprove1_savingAmount: (value) => set((state) => {
        return { contentApprove1_savingAmount: value }
    }),

    //Your operation is classified as:
    contentApprove2_Operation_List: [
        {
            "value": "",
            "label": "Please Select",
        },
        {
            "value": "Minor",
            "label": "Minor"
        },
        {
            "value": "Inter",
            "label": "Inter"
        },
        {
            "value": "Major",
            "label": "Major"
        },
        {
            "value": "Complex",
            "label": "Complex"
        },
        {
            "value": "NA",
            "label": "NA"
        },
        // {
        //     "value": "Not Applicable",
        //     "label": "Not Applicable"
        // }
    ],
    contentApprove2_Operation: {
        "value": "",
        "label": "Please Select",
    },
    setContentApprove2_Operation: (value) => set((state) => {
        return { contentApprove2_Operation: value }
    }),
    isValid_contentApprove2_Operation: true,
    setIsValid_contentApprove2_Operation: (value) => set((state) => {
        return { isValid_contentApprove2_Operation: value }
    }),

    //Entitled Level of Accommodation
    contentApprove2_Accommodation_List: [
        {
            value: "",
            label: "Please Select",
        },
        {
            value: "Ward",
            label: "Ward",
        },
        {
            value: "Semi-Private",
            label: "Semi-Private",
        },
        {
            value: "Private",
            label: "Private",
        },
        {
            value: "Daycase",
            label: "Daycase",
        },
        {
            value: "Clinical",
            label: "Clinical",
        },
    ],
    contentApprove2_Accommodation: {
        "value": "",
        "label": "Please Select",
    },
    setContentApprove2_Accommodation: (value) => set((state) => {
        return { contentApprove2_Accommodation: value }
    }),
    isValid_contentApprove2_Accommodation: true,
    setIsValid_contentApprove2_Accommodation: (value) => set((state) => {
        return { isValid_contentApprove2_Accommodation: value }
    }),

    isValid_contentSMMBenefit_overallLimit_type: true,
    setIsValid_contentSMMBenefit_overallLimit_type: (value) => set((state) => {
        return { isValid_contentSMMBenefit_overallLimit_type: value }
    }),

    //Benefit Type
    contentApprove2_BenefitType_List: [
        {
            value: "",
            label: "Please Select",
        },
        {
            value: "Lum Sum Benefit",
            label: "Lum Sum Benefit",
        },
        {
            value: "Benefit Under Basic Benefit",
            label: "Benefit Under Basic Benefit",
        },
    ],
    contentApprove2_BenefitType: {
        value: "",
        label: "Please Select",
    },
    setContentApprove2_BenefitType: (value) => set((state) => {
        return { contentApprove2_BenefitType: value }
    }),
    isValid_contentApprove2_BenefitType: true,
    setIsValid_contentApprove2_BenefitType: (value) => set((state) => {
        return { isValid_contentApprove2_BenefitType: value }
    }),

    //Lump Sum Benefit Amount
    contentApprove2_lumpSumBenefitAmount: "",
    setContentApprove2_lumpSumBenefitAmount: (value) => set((state) => {
        return { contentApprove2_lumpSumBenefitAmount: value }
    }),
    isValid_contentApprove2_lumpSumBenefitAmount: true,
    setIsValid_contentApprove2_lumpSumBenefitAmount: (value) => set((state) => {
        return { isValid_contentApprove2_lumpSumBenefitAmount: value }
    }),

    contentApprove3_roomClass_option1: "",
    contentApprove3_roomClass_option2: "",
    contentApprove3_roomClass_option3: "",
    setContentApprove3_roomClass_option1: (value) => set((state) => {
        return { contentApprove3_roomClass_option1: value }
    }),
    setContentApprove3_roomClass_option2: (value) => set((state) => {
        return { contentApprove3_roomClass_option2: value }
    }),
    setContentApprove3_roomClass_option3: (value) => set((state) => {
        return { contentApprove3_roomClass_option3: value }
    }),
    isValid_contentApprove3_roomClass_option: true,
    setIsValid_contentApprove3_roomClass_option: (value) => set((state) => {
        return { isValid_contentApprove3_roomClass_option: value }
    }),

    contentApprove3_logAmount: "",
    setContentApprove3_logAmount: (value) => set((state) => {
        return { contentApprove3_logAmount: value }
    }),
    isValid_contentApprove3_logAmount: true,
    setIsValid_contentApprove3_logAmount: (value) => set((state) => {
        return { isValid_contentApprove3_logAmount: value }
    }),

    // Surgeon's Fee
    contentBasicHospitalAndSurgicalBenefits_surgeon_fee: "",
    setContentBasicHospitalAndSurgicalBenefits_surgeon_fee: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_surgeon_fee: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark: value }
    }),

    // Anaesthetist's Fee
    contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: "",
    setContentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark: value }
    }),

    // Operating Theatre
    contentBasicHospitalAndSurgicalBenefits_operating_theatre: "",
    setContentBasicHospitalAndSurgicalBenefits_operating_theatre: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_operating_theatre: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_operating_theatre_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark: value }
    }),

    // Miscellaneous Hospital Charges
    contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: "",
    setContentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark: value }
    }),

    // Physician's Hospital Visit (Per Days)
    contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: "",
    setContentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark: value }
    }),

    // 住院日數 cnofdays
    contentBasicHospitalAndSurgicalBenefits_cnofdays: "",
    setContentBasicHospitalAndSurgicalBenefits_cnofdays: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_cnofdays: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_cnofdays: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_cnofdays: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_cnofdays: value }
    }),

    // Room and Board
    contentBasicHospitalAndSurgicalBenefits_room_and_board: "",
    setContentBasicHospitalAndSurgicalBenefits_room_and_board: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_room_and_board: value }
    }),
    isValid_contentBasicHospitalAndSurgicalBenefits_room_and_board: true,
    setIsValid_contentBasicHospitalAndSurgicalBenefits_room_and_board: (value) => set((state) => {
        return { isValid_contentBasicHospitalAndSurgicalBenefits_room_and_board: value }
    }),

    contentBasicHospitalAndSurgicalBenefits_room_and_board_remark: "",
    setContentBasicHospitalAndSurgicalBenefits_room_and_board_remark: (value) => set((state) => {
        return { contentBasicHospitalAndSurgicalBenefits_room_and_board_remark: value }
    }),


    contentSMMBenefit_fillInSMMDetail: false,
    setContentSMMBenefit_fillInSMMDetail: (value) => set((state) => {
        return { contentSMMBenefit_fillInSMMDetail: value }
    }),

    contentSMMBenefit_deductibleAmount: "",
    setContentSMMBenefit_deductibleAmount: (value) => set((state) => {
        return { contentSMMBenefit_deductibleAmount: value }
    }),

    contentSMMBenefit_reimbursementPercentage: "",
    setContentSMMBenefit_reimbursementPercentage: (value) => set((state) => {
        return { contentSMMBenefit_reimbursementPercentage: value }
    }),

    // contentSMMBenefit_overallLimit_type_list: [
    //     { label: i18n.t("translation:dropdownlist.contentSMMBenefit_overallLimit_type_list1"), value: "DISABILITY", color: "var(--aia-bx-dark-blue)" },
    //     { label: i18n.t("translation:dropdownlist.contentSMMBenefit_overallLimit_type_list2"), value: "PERYEAR", color: "var(--aia-bx-dark-blue)" },
    //     { label: i18n.t("translation:dropdownlist.contentSMMBenefit_overallLimit_type_list3"), value: "CONTRACTYEAR", color: "var(--aia-bx-dark-blue)" },
    //     { label: i18n.t("translation:dropdownlist.contentSMMBenefit_overallLimit_type_list4"), value: "SURGERY", color: "var(--aia-bx-dark-blue)" },
    //     { label:  "N/A", value: "NA", color: "var(--aia-bx-dark-blue)" },
    //  ],
     contentSMMBenefit_overallLimit_type_list: [
        { label:  "Please Select", value: "", color: "var(--aia-bx-dark-blue)" },
        { label:"per disability", value: "DISABILITY", color: "var(--aia-bx-dark-blue)" },
        { label: "per policy year", value: "PERYEAR", color: "var(--aia-bx-dark-blue)" },
        { label: "per disability per policy year", value: "CONTRACTYEAR", color: "var(--aia-bx-dark-blue)" },
        { label: "per surgery", value: "SURGERY", color: "var(--aia-bx-dark-blue)" },
        { label:  "N/A", value: "NA", color: "var(--aia-bx-dark-blue)" },
     ],
    contentSMMBenefit_overallLimit_type: {
        "label": "Please Select",
        "value": "",
        "color": "black"
    },
    setContentSMMBenefit_overallLimitType: (value) => set((state) => {
        return { contentSMMBenefit_overallLimit_type: value }
    }),

    contentSMMBenefit_overallLimit: "",
    setContentSMMBenefit_overallLimit: (value) => set((state) => {
        return { contentSMMBenefit_overallLimit: value }
    }),


    contentSMMBenefit_remarks: [],
    setContentSMMBenefit_remarks: (value) => set((state) => {
        return { contentSMMBenefit_remarks: value }
    }),

    contentSMMBenefit_remarks_text: "",
    setContentSMMBenefit_remarksText: (value) => set((state) => {
        return { contentSMMBenefit_remarks_text: value }
    }),

    //Email Template
    contentComposeEmail_Template_List: [
        {
            "label": "Please Select Email Template",
            "value": ""
        },
        {
            "label": "Request for supplementary information Email",
            "value": "REQSUP"
        },
        {
            "label": "Approved the request and completed the LOG action (with LOG and LOG pw)",
            "value": "APPLOGCPTACTION"
        },
        {
            "label": "Approved PCA request with estimated claims fee",
            "value": "APPPCA"
        },
        {
            "label": "Rejected LOG request but provide PCA recommendations",
            "value": "CHGPCA"
        },
        {
            "label": "Decline the request - Pre-auth ver.",
            "value": "DECLINEDLOG"
        },
        {
            "label": "Decline the request - PCA ver.",
            "value": "DECLINEDPCA"
        },
        {
            "label": "Resend LOG email",
            "value": "RESENDLOG"
        },
        {
            "label": "Resend PCA email",
            "value": "RESENDPCA"
        },
    ],
    contentComposeEmail_Template: {
        "label": "Please Select Email Template",
        "value": ""
    },
    setContentComposeEmail_Template: (value) => set((state) => {
        return { contentComposeEmail_Template: value }
    }),
    isValidEmailTemplate: true,
    setIsValidEmailTemplate: (value) => set((state) => {
        return { isValidEmailTemplate: value }
    }),

    //Reason for Decline and remarks
    contentComposeEmail_Reason_List: [
        { "label": i18n.t("translation:admin.modal.email.reason.P001"), "value": "P001", },
        { "label": i18n.t("translation:admin.modal.email.reason.P002"), "value": "P002", },
        { "label": i18n.t("translation:admin.modal.email.reason.P003"), "value": "P003", },
        { "label": i18n.t("translation:admin.modal.email.reason.P004"), "value": "P004", },
        { "label": i18n.t("translation:admin.modal.email.reason.P005"), "value": "P005", },
        { "label": i18n.t("translation:admin.modal.email.reason.P006"), "value": "P006", },
        { "label": i18n.t("translation:admin.modal.email.reason.P007"), "value": "P007", },
        { "label": i18n.t("translation:admin.modal.email.reason.P008"), "value": "P008", },
        { "label": i18n.t("translation:admin.modal.email.reason.P009"), "value": "P009", },
        { "label": i18n.t("translation:admin.modal.email.reason.P010"), "value": "P010", },
        { "label": i18n.t("translation:admin.modal.email.reason.P011"), "value": "P011", },
        { "label": i18n.t("translation:admin.modal.email.reason.P012"), "value": "P012", },
        { "label": i18n.t("translation:admin.modal.email.reason.P013"), "value": "P013", },
        { "label": i18n.t("translation:admin.modal.email.reason.P014"), "value": "P014", },
        { "label": i18n.t("translation:admin.modal.email.reason.P015"), "value": "P015", },
        { "label": i18n.t("translation:admin.modal.email.reason.P016"), "value": "P016", },
        { "label": i18n.t("translation:admin.modal.email.reason.P017"), "value": "P017", },
        { "label": i18n.t("translation:admin.modal.email.reason.P018"), "value": "P018", },
        { "label": i18n.t("translation:admin.modal.email.reason.P019"), "value": "P019", },
        { "label": i18n.t("translation:admin.modal.email.reason.P020"), "value": "P020", },
        { "label": i18n.t("translation:admin.modal.email.reason.P021"), "value": "P021", },
        { "label": i18n.t("translation:admin.modal.email.reason.P022"), "value": "P022", },
        { "label": i18n.t("translation:admin.modal.email.reason.P023"), "value": "P023", },
        { "label": i18n.t("translation:admin.modal.email.reason.P024"), "value": "P024", },
        { "label": i18n.t("translation:admin.modal.email.reason.P025"), "value": "P025", },
        { "label": i18n.t("translation:admin.modal.email.reason.P026"), "value": "P026", },
        { "label": i18n.t("translation:admin.modal.email.reason.P027"), "value": "P027", },
        { "label": i18n.t("translation:admin.modal.email.reason.P028"), "value": "P028", },
        { "label": i18n.t("translation:admin.modal.email.reason.P029"), "value": "P029", },
        { "label": i18n.t("translation:admin.modal.email.reason.P030"), "value": "P030", },
        { "label": i18n.t("translation:admin.modal.email.reason.P031"), "value": "P031", },
        { "label": i18n.t("translation:admin.modal.email.reason.P032"), "value": "P032", },
        { "label": i18n.t("translation:admin.modal.email.reason.P033"), "value": "P033", },
        { "label": i18n.t("translation:admin.modal.email.reason.P034"), "value": "P034", },
        { "label": i18n.t("translation:admin.modal.email.reason.P035"), "value": "P035", },
        { "label": i18n.t("translation:admin.modal.email.reason.P036"), "value": "P036", },
        { "label": i18n.t("translation:admin.modal.email.reason.P037"), "value": "P037", },
        { "label": i18n.t("translation:admin.modal.email.reason.P038"), "value": "P038", },
        { "label": i18n.t("translation:admin.modal.email.reason.P039"), "value": "P039", },
        { "label": i18n.t("translation:admin.modal.email.reason.P040"), "value": "P040", },
        { "label": i18n.t("translation:admin.modal.email.reason.P041"), "value": "P041", },
        { "label": i18n.t("translation:admin.modal.email.reason.P042"), "value": "P042", },
        { "label": i18n.t("translation:admin.modal.email.reason.P043"), "value": "P043", },
        { "label": i18n.t("translation:admin.modal.email.reason.P044"), "value": "P044", },
        { "label": i18n.t("translation:admin.modal.email.reason.P045"), "value": "P045", },
        { "label": i18n.t("translation:admin.modal.email.reason.P046"), "value": "P046", },
        { "label": i18n.t("translation:admin.modal.email.reason.P047"), "value": "P047", },
        { "label": i18n.t("translation:admin.modal.email.reason.P048"), "value": "P048", },
        { "label": i18n.t("translation:admin.modal.email.reason.P049"), "value": "P049", },
        { "label": i18n.t("translation:admin.modal.email.reason.P050"), "value": "P050", },
        { "label": i18n.t("translation:admin.modal.email.reason.P051"), "value": "P051", },
        { "label": i18n.t("translation:admin.modal.email.reason.P052"), "value": "P052", },
        { "label": i18n.t("translation:admin.modal.email.reason.P053"), "value": "P053", },
        { "label": i18n.t("translation:admin.modal.email.reason.P054"), "value": "P054", },
        { "label": i18n.t("translation:admin.modal.email.reason.P055"), "value": "P055", },
        { "label": i18n.t("translation:admin.modal.email.reason.P056"), "value": "P056", },
        { "label": i18n.t("translation:admin.modal.email.reason.P057"), "value": "P057", },
        { "label": i18n.t("translation:admin.modal.email.reason.P058"), "value": "P058", },
        { "label": i18n.t("translation:admin.modal.email.reason.P059"), "value": "P059", },
        { "label": i18n.t("translation:admin.modal.email.reason.P060"), "value": "P060", },
        { "label": i18n.t("translation:admin.modal.email.reason.P061"), "value": "P061", },
        { "label": i18n.t("translation:admin.modal.email.reason.P062"), "value": "P062", },
        { "label": i18n.t("translation:admin.modal.email.reason.P063"), "value": "P063", },
        { "label": i18n.t("translation:admin.modal.email.reason.P064"), "value": "P064", },
        { "label": i18n.t("translation:admin.modal.email.reason.P065"), "value": "P065", },
        { "label": i18n.t("translation:admin.modal.email.reason.P066"), "value": "P066", },
        { "label": i18n.t("translation:admin.modal.email.reason.P067"), "value": "P067", },
        { "label": i18n.t("translation:admin.modal.email.reason.P068"), "value": "P068", },
        { "label": i18n.t("translation:admin.modal.email.reason.P069"), "value": "P069", },
        { "label": i18n.t("translation:admin.modal.email.reason.P070"), "value": "P070", },
        { "label": i18n.t("translation:admin.modal.email.reason.P071"), "value": "P071", },
        { "label": i18n.t("translation:admin.modal.email.reason.P072"), "value": "P072", },
        { "label": i18n.t("translation:admin.modal.email.reason.P073"), "value": "P073", },
        { "label": i18n.t("translation:admin.modal.email.reason.P074"), "value": "P074", },
        { "label": i18n.t("translation:admin.modal.email.reason.P075"), "value": "P075", },
        { "label": i18n.t("translation:admin.modal.email.reason.P076"), "value": "P076", },
        { "label": i18n.t("translation:admin.modal.email.reason.P077"), "value": "P077", },
        { "label": i18n.t("translation:admin.modal.email.reason.P078"), "value": "P078", },
        { "label": i18n.t("translation:admin.modal.email.reason.P079"), "value": "P079", },
        { "label": i18n.t("translation:admin.modal.email.reason.P080"), "value": "P080", },
        { "label": i18n.t("translation:admin.modal.email.reason.P081"), "value": "P081", },
        { "label": i18n.t("translation:admin.modal.email.reason.P082"), "value": "P082", },
        { "label": i18n.t("translation:admin.modal.email.reason.P083"), "value": "P083", },
        { "label": i18n.t("translation:admin.modal.email.reason.P084"), "value": "P084", },
        { "label": i18n.t("translation:admin.modal.email.reason.P085"), "value": "P085", },
        { "label": i18n.t("translation:admin.modal.email.reason.P086"), "value": "P086", },
        { "label": i18n.t("translation:admin.modal.email.reason.P087"), "value": "P087", },
        { "label": i18n.t("translation:admin.modal.email.reason.P088"), "value": "P088", },
        { "label": i18n.t("translation:admin.modal.email.reason.P089"), "value": "P089", },
        { "label": i18n.t("translation:admin.modal.email.reason.P090"), "value": "P090", },
        { "label": i18n.t("translation:admin.modal.email.reason.P091"), "value": "P091", },
        { "label": i18n.t("translation:admin.modal.email.reason.P092"), "value": "P092", },
        { "label": i18n.t("translation:admin.modal.email.reason.P093"), "value": "P093", },
        { "label": i18n.t("translation:admin.modal.email.reason.P094"), "value": "P094", },
        { "label": i18n.t("translation:admin.modal.email.reason.P095"), "value": "P095", },
        { "label": i18n.t("translation:admin.modal.email.reason.P096"), "value": "P096", },
        { "label": i18n.t("translation:admin.modal.email.reason.P097"), "value": "P097", },
        { "label": i18n.t("translation:admin.modal.email.reason.R001"), "value": "R001", },
        { "label": i18n.t("translation:admin.modal.email.reason.R002"), "value": "R002", },
        { "label": i18n.t("translation:admin.modal.email.reason.R003"), "value": "R003", },
        { "label": i18n.t("translation:admin.modal.email.reason.R004"), "value": "R004", },
        { "label": i18n.t("translation:admin.modal.email.reason.R005"), "value": "R005", },
        { "label": i18n.t("translation:admin.modal.email.reason.R006"), "value": "R006", },
        { "label": i18n.t("translation:admin.modal.email.reason.R007"), "value": "R007", },
        { "label": i18n.t("translation:admin.modal.email.reason.R008"), "value": "R008", },
        { "label": i18n.t("translation:admin.modal.email.reason.R009"), "value": "R009", },
        { "label": i18n.t("translation:admin.modal.email.reason.R010"), "value": "R010", },
    ],
    contentComposeEmail_Reason: [],
    setContentComposeEmail_Reason: (value) => set((state) => {
        return { contentComposeEmail_Reason: value }
    }),

    //Email Title
    contentComposeEmail_Title: "",
    setContentComposeEmail_Title: (value) => set((state) => {
        return { contentComposeEmail_Title: value }
    }),
    isValidEmailTitle: true,
    setIsValidEmailTitle: (value) => set((state) => {
        return { isValidEmailTitle: value }
    }),

    contentComposeEmail_Content: "",

    admUpdatePreAuthClaim: async (aid, field) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { resetAllPopupValue } = get();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admUpdatePreAuthClaim`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "aid": aid,
                ...field
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        resetAllPopupValue();

        return data;

    },

    //Incurred Amount
    contentClaimRequest_Incurred_amount: "",
    setContentClaimRequest_Incurred_amount: (value) => set((state) => {
        return { contentClaimRequest_Incurred_amount: value }
    }),
    isValid_contentClaimRequest_Incurred_amount: true,
    setIsValid_contentClaimRequest_Incurred_amount: (value) => set((state) => {
        return { isValid_contentClaimRequest_Incurred_amount: value }
    }),

    //Eligible Amount
    contentClaimRequest_paid_amount: "",
    setContentClaimRequest_paid_amount: (value) => set((state) => {
        return { contentClaimRequest_paid_amount: value }
    }),
    isValid_contentClaimRequest_paid_amount: true,
    setIsValid_contentClaimRequest_paid_amount: (value) => set((state) => {
        return { isValid_contentClaimRequest_paid_amount: value }
    }),

    //Chargeback
    contentClaimRequest_chargeback: "",
    setContentClaimRequest_chargeback: (value) => set((state) => {
        return { contentClaimRequest_chargeback: value }
    }),
    isValid_contentClaimRequest_chargeback: true,
    setIsValid_contentClaimRequest_chargeback: (value) => set((state) => {
        return { isValid_contentClaimRequest_chargeback: value }
    }),

    //WIS No
    contentClaimRequest_wis_no: "",
    setContentClaimRequest_wis_no: (value) => set((state) => {
        return { contentClaimRequest_wis_no: value }
    }),
    isValid_contentClaimRequest_wis_no: true,
    setIsValid_contentClaimRequest_wis_no: (value) => set((state) => {
        return { isValid_contentClaimRequest_wis_no: value }
    }),

    //Debit no.
    contentClaimRequest_debit_no: "",
    setContentClaimRequest_debit_no: (value) => set((state) => {
        return { contentClaimRequest_debit_no: value }
    }),
    isValid_contentClaimRequest_debit_no: true,
    setIsValid_contentClaimRequest_debit_no: (value) => set((state) => {
        return { isValid_contentClaimRequest_debit_no: value }
    }),

    // Chargeback Datetime
    contentScheduledChargeback_Chargeback_datatime: "",
    setContentScheduledChargeback_Chargeback_datatime: (value) => set((state) => {
        return { contentScheduledChargeback_Chargeback_datatime: value }
    }),
    isValid_contentScheduledChargeback_Chargeback_datatime: true,
    setIsValid_contentScheduledChargeback_Chargeback_datatime: (value) => set((state) => {
        return { isValid_contentScheduledChargeback_Chargeback_datatime: value }
    }),

    taskList_activeKey: "",
    setTaskList_activeKey: (value) => set((state) => {
        return { taskList_activeKey: value }
    }),

    openContentApprove1Modal: async (aid, value) => {
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        const { openContentApprove2Modal } = get();

        openModalConfirm({
            title: "Approve Request",
            modalConfirmPrimaryButtonText: "Next",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentApprove1 />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: () => {
                const { contentApprove1_TreatmentType, setIsValid_contentApprove1_TreatmentType } = useAdminApplicationDetailStore.getState();
                const { contentApprove1_ClassificationOfSurgery, setIsValid_contentApprove1_ClassificationOfSurgery } = useAdminApplicationDetailStore.getState();

                setIsValid_contentApprove1_TreatmentType(true);
                setIsValid_contentApprove1_ClassificationOfSurgery(true);

                let isValid = true;

                if (!contentApprove1_TreatmentType?.value) {
                    setIsValid_contentApprove1_TreatmentType(false);
                    isValid = false;
                }

                if (!contentApprove1_ClassificationOfSurgery?.value) {
                    setIsValid_contentApprove1_ClassificationOfSurgery(false);
                    isValid = false;
                }

                if (isValid) {
                    closeModalConfirm();
                    openContentApprove2Modal(aid, value);
                }
            },
        });
    },

    openContentApprove2Modal: (aid, value) => {
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        const { openContentApprove1Modal, openContentSMMBenefitModal, openContentBasicHospitalAndSurgicalBenefitsModal } = get();

        const isPCA = value.value === "APPROVEPCA" || value.value === "CHGTOPCA";

        openModalConfirm({
            title: "Approve Request",
            modalConfirmPrimaryButtonText: "Next",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentApprove2 isPCA={isPCA} />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: () => {
                const { contentApprove2_Operation, contentApprove2_Accommodation, contentApprove2_BenefitType, contentApprove2_lumpSumBenefitAmount } = useAdminApplicationDetailStore.getState();
                const { setIsValid_contentApprove2_Operation, setIsValid_contentApprove2_Accommodation, setIsValid_contentApprove2_BenefitType, setIsValid_contentApprove2_lumpSumBenefitAmount,setIsValid_contentSMMBenefit_overallLimit_type,contentSMMBenefit_overallLimit_type } = useAdminApplicationDetailStore.getState();

                setIsValid_contentApprove2_Operation(true);
                setIsValid_contentApprove2_Accommodation(true); 
                setIsValid_contentApprove2_BenefitType(true);
                setIsValid_contentApprove2_lumpSumBenefitAmount(true);

                let isValid = true;

                if (isPCA && !contentApprove2_Operation?.value) {
                    setIsValid_contentApprove2_Operation(false);
                    isValid = false;
                }

                if (!contentApprove2_Accommodation?.value) {
                    setIsValid_contentApprove2_Accommodation(false);
                    isValid = false;
                }
                if (contentSMMBenefit_overallLimit_type?.value ==='') { 
                    setIsValid_contentSMMBenefit_overallLimit_type(false);
                    isValid = false;
                }

                if (!contentApprove2_BenefitType?.value) {
                    setIsValid_contentApprove2_BenefitType(false);
                    isValid = false;
                }

                if (contentApprove2_BenefitType?.value === "Lum Sum Benefit" && !contentApprove2_lumpSumBenefitAmount) {
                    setIsValid_contentApprove2_lumpSumBenefitAmount(false);
                    isValid = false;
                }

                if (isValid) {
                    closeModalConfirm();

                    if (contentApprove2_BenefitType?.value === "Lum Sum Benefit") {
                        openContentSMMBenefitModal(aid, value);
                    } else {
                        openContentBasicHospitalAndSurgicalBenefitsModal(aid, value);
                    }
                }
            },
            cancelCallback: () => {
                if (value.value === "APPROVELOG" || value.value === "APPROVEPCA" || value.value === "CHGTOPCA" || value.value === "CHGTOLOG") {
                    openContentApprove1Modal(aid, value);
                }
            },
        });
    },

    openContentApprove3Modal: (aid, value) => {
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        const { openContentSMMBenefitModal, openEmailTemplate } = get();

        openModalConfirm({
            title: "Approve Request",
            modalConfirmPrimaryButtonText: "Next",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentApprove3 />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
                const isChangeToLOG = value.value === "CHGTOLOG";
                const { contentApprove3_logAmount, setIsValid_contentApprove3_logAmount } = useAdminApplicationDetailStore.getState();

                let isValid = true;

                // if (!contentApprove3_roomClass_option1 && !contentApprove3_roomClass_option2 && !contentApprove3_roomClass_option3) {
                //     setIsValid_contentApprove3_roomClass_option(false);
                //     isValid = false;
                // }

                if (!contentApprove3_logAmount) {
                    setIsValid_contentApprove3_logAmount(false);
                    isValid = false;
                }

                if (isValid) {
                    const loadEmailTemplate = withErrorHandling(openEmailTemplate);
                    loadEmailTemplate(aid, "APPLOGCPTACTION");

                    openModalConfirm({
                        title: isChangeToLOG ? "Change to LOG" : "Approve LOG",
                        modalConfirmPrimaryButtonStyleChangeToDanger: true,
                        modalConfirmPrimaryButtonText: "Approve",
                        modalConfirmSecondaryButtonText: "Back",
                        content: <ContentComposeEmail disabled={true} showReason={true} />,
                        closeManually: true,
                        style: { content: { width: 720 } },
                        callback: async () => {

                            const { data, admUpdatePreAuthClaim, admChgPreAuthFormStatus, admGetPreAuthForm } = get();
                            const { openModalInfo } = useModalStore.getState();
                            const { contentApprove1_TreatmentType, contentApprove1_ClassificationOfSurgery, contentApprove1_ICD, contentApprove1_ICD_code, contentApprove1_Remarks, contentApprove1_Remarks_text, contentApprove1_savingAmount } = useAdminApplicationDetailStore.getState();
                            const { contentApprove2_Operation, contentApprove2_Accommodation, contentApprove2_BenefitType, contentApprove2_lumpSumBenefitAmount } = useAdminApplicationDetailStore.getState();
                            const { contentSMMBenefit_fillInSMMDetail, contentSMMBenefit_deductibleAmount, contentSMMBenefit_reimbursementPercentage, contentSMMBenefit_overallLimit_type, contentSMMBenefit_overallLimit, contentSMMBenefit_remarks, contentSMMBenefit_remarks_text } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_surgeon_fee, contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee, contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_operating_theatre, contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges, contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit, contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_cnofdays } = get();
                            const { contentBasicHospitalAndSurgicalBenefits_room_and_board, contentBasicHospitalAndSurgicalBenefits_room_and_board_remark } = get();

                            const { contentApprove3_roomClass_option1, contentApprove3_roomClass_option2, contentApprove3_roomClass_option3, setIsValid_contentApprove3_roomClass_option } = useAdminApplicationDetailStore.getState();
                            const { contentApprove3_logAmount, setIsValid_contentApprove3_logAmount } = useAdminApplicationDetailStore.getState();
                            const { admSendEmail } = get();

                            const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
                            const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
                            const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);
                            const loadAdmSendEmail = withErrorHandling(admSendEmail);

                            if (isValid) {
                                await loadAdmUpdatePreAuthClaim(aid, {
                                    //ContentApprove1
                                    treatmentType: contentApprove1_TreatmentType?.value ?? "",
                                    classificationSurgery: contentApprove1_ClassificationOfSurgery?.value ?? "",
                                    ICDCode: contentApprove1_ICD ?? "",
                                    ICDCodeList: contentApprove1_ICD_code.join(","),
                                    treatmentRemarkOption: contentApprove1_Remarks?.join(","),
                                    treatmentRemark: contentApprove1_Remarks_text ?? "",
                                    saveAmt: contentApprove1_savingAmount ?? "",

                                    //ContentApprove2
                                    operationClassified: contentApprove2_Operation?.value ?? "",
                                    lvOfAccommodation: contentApprove2_Accommodation?.value ?? "",
                                    benefitType: contentApprove2_BenefitType?.value ?? "",
                                    lumpSumAmt: contentApprove2_lumpSumBenefitAmount,

                                    //ContentBasicHospitalAndSurgicalBenefits
                                    surgeonFee: contentBasicHospitalAndSurgicalBenefits_surgeon_fee ?? "",
                                    surgeonFeeRemark: contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark ?? "",
                                    anaesthetistFee: contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee ?? "",
                                    anaesthetistFeeRemark: contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark ?? "",
                                    operatingTheatreFee: contentBasicHospitalAndSurgicalBenefits_operating_theatre ?? "",
                                    operatingTheatreFeeRemark: contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark ?? "",
                                    miscellaneousHospitalCharge: contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges ?? "",
                                    miscellaneousHospitalChargeRemark: contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark ?? "",
                                    physicianFee: contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit ?? "",
                                    physicianFeeRemark: contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark ?? "",
                                    cnofdays: contentBasicHospitalAndSurgicalBenefits_cnofdays ?? "",
                                    roomFee: contentBasicHospitalAndSurgicalBenefits_room_and_board ?? "",
                                    roomFeeRemark: contentBasicHospitalAndSurgicalBenefits_room_and_board_remark ?? "",

                                    //ContentSMMBenefit
                                    SMMDetail: contentSMMBenefit_fillInSMMDetail ? "Y" : "N",
                                    deductAmt: contentSMMBenefit_deductibleAmount,
                                    reimbursementPercent: contentSMMBenefit_reimbursementPercentage,
                                    overAllLimitType: contentSMMBenefit_overallLimit_type?.value,
                                    overallLimit: contentSMMBenefit_overallLimit,
                                    SMMRemarkOption: contentSMMBenefit_remarks.join(","),
                                    SMMRemark: contentSMMBenefit_remarks_text,

                                    //ContentApprove3
                                    roomClass: contentApprove3_roomClass_option1 ? "Ward" : contentApprove3_roomClass_option2 ? "Semi-private" : "private",
                                    LOGAmt: contentApprove3_logAmount,
                                });

                                if (value.value === "CHGTOLOG") {

                                    await loadAdmChgPreAuthFormStatus(aid, { value: "CHGTOLOG" })
                                    await loadAdmSendEmail(aid);

                                    closeModalConfirm();
                                    loadAdmGetPreAuthForm(aid).then(() => {
                                        openModalInfo({
                                            title: "Change to LOG",
                                            isShowSuccessIcon: true,
                                            text: <Flex direction={"column"}>
                                                <FormText>Request is approved and the LOG has been sent to : </FormText>
                                                <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span>
                                            </Flex>,
                                            style: { content: { width: 480 } },
                                        });
                                    });

                                } else {

                                    await loadAdmChgPreAuthFormStatus(aid, { value: "APPROVELOG" })
                                    await loadAdmSendEmail(aid);

                                    closeModalConfirm();
                                    loadAdmGetPreAuthForm(aid).then(() => {
                                        openModalInfo({
                                            title: "Approve Request",
                                            isShowSuccessIcon: true,
                                            text: <Flex direction={"column"}>
                                                <FormText>Request is approved and the LOG has been sent to : </FormText>
                                                <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span>
                                            </Flex>,
                                            style: { content: { width: 480 } },
                                        });
                                    });

                                }
                            }
                        },
                        cancelCallback: () => {
                            const { openContentApprove3Modal } = get();
                            openContentApprove3Modal(aid, value);
                        },
                    });
                }

            },
            cancelCallback: () => {
                openContentSMMBenefitModal(aid, value);
            },
        });
    },

    openContentSMMBenefitModal: (aid, value) => {
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        const { data, setIsValidEmailTitle, openEmailTemplate } = get();
        const { contentBasicHospitalAndSurgicalBenefits_cnofdays } = get();

        const loadEmailTemplate = withErrorHandling(openEmailTemplate);

        openModalConfirm({
            title: "Supplementary Major Medical (SMM) Benefit",
            modalConfirmPrimaryButtonText: "Next",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentSMMBenefit />,
            style: { content: { width: 720 } },
            callback: async () => {
                if (value.value === "CHGTOPCA" || value.value === "APPROVEPCA") {
                    const isChangeToPCA = value.value === "CHGTOPCA";

                    loadEmailTemplate(aid, isChangeToPCA ? "CHGPCA" : "APPPCA");

                    openModalConfirm({
                        title: isChangeToPCA ? "Change to PCA" : "Approve PCA",
                        modalConfirmPrimaryButtonStyleChangeToDanger: true,
                        modalConfirmPrimaryButtonText: "Approve",
                        modalConfirmSecondaryButtonText: "Back",
                        content: <ContentComposeEmail disabled={true} showReason={true} />,
                        closeManually: true,
                        style: { content: { width: 720 } },
                        callback: async () => {
                            const { openModalInfo } = useModalStore.getState();
                            const { contentComposeEmail_Title, admGetPreAuthForm } = useAdminApplicationDetailStore.getState();
                            const { contentApprove1_TreatmentType, contentApprove1_ClassificationOfSurgery, contentApprove1_ICD, contentApprove1_ICD_code, contentApprove1_Remarks, contentApprove1_Remarks_text, contentApprove1_savingAmount } = useAdminApplicationDetailStore.getState();
                            const { contentApprove2_Operation, contentApprove2_Accommodation, contentApprove2_BenefitType, contentApprove2_lumpSumBenefitAmount } = useAdminApplicationDetailStore.getState();
                            const { contentSMMBenefit_fillInSMMDetail, contentSMMBenefit_deductibleAmount, contentSMMBenefit_reimbursementPercentage, contentSMMBenefit_overallLimit_type, contentSMMBenefit_overallLimit, contentSMMBenefit_remarks, contentSMMBenefit_remarks_text } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_surgeon_fee, contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee, contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_operating_theatre, contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges, contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark } = useAdminApplicationDetailStore.getState();
                            const { contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit, contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark } = useAdminApplicationDetailStore.getState();
                            const { admSendEmail, admUpdatePreAuthClaim, admChgPreAuthFormStatus } = get();
                            const { contentBasicHospitalAndSurgicalBenefits_room_and_board, contentBasicHospitalAndSurgicalBenefits_room_and_board_remark } = get();

                            const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
                            const loadAdmSendEmail = withErrorHandling(admSendEmail);
                            const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
                            const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);

                            let isValid = true;

                            if (!contentComposeEmail_Title) {
                                setIsValidEmailTitle(false);
                                isValid = false;
                            }

                            if (isValid) {

                                await loadAdmUpdatePreAuthClaim(aid, {
                                    //ContentApprove1
                                    treatmentType: contentApprove1_TreatmentType?.value ?? "",
                                    classificationSurgery: contentApprove1_ClassificationOfSurgery?.value ?? "",
                                    ICDCode: contentApprove1_ICD ?? "",
                                    ICDCodeList: contentApprove1_ICD_code.join(","),
                                    treatmentRemarkOption: contentApprove1_Remarks.join(","),
                                    treatmentRemark: contentApprove1_Remarks_text ?? "",
                                    saveAmt: contentApprove1_savingAmount ?? "",

                                    //ContentApprove2
                                    operationClassified: contentApprove2_Operation?.value ?? "",
                                    lvOfAccommodation: contentApprove2_Accommodation?.value ?? "",
                                    benefitType: contentApprove2_BenefitType?.value ?? "",
                                    lumpSumAmt: contentApprove2_lumpSumBenefitAmount,

                                    //ContentBasicHospitalAndSurgicalBenefits
                                    surgeonFee: contentBasicHospitalAndSurgicalBenefits_surgeon_fee ?? "",
                                    surgeonFeeRemark: contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark ?? "",
                                    anaesthetistFee: contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee ?? "",
                                    anaesthetistFeeRemark: contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark ?? "",
                                    operatingTheatreFee: contentBasicHospitalAndSurgicalBenefits_operating_theatre ?? "",
                                    operatingTheatreFeeRemark: contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark ?? "",
                                    miscellaneousHospitalCharge: contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges ?? "",
                                    miscellaneousHospitalChargeRemark: contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark ?? "",
                                    physicianFee: contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit ?? "",
                                    physicianFeeRemark: contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark ?? "",
                                    cnofdays: contentBasicHospitalAndSurgicalBenefits_cnofdays ?? "",
                                    roomFee: contentBasicHospitalAndSurgicalBenefits_room_and_board ?? "",
                                    roomFeeRemark: contentBasicHospitalAndSurgicalBenefits_room_and_board_remark ?? "",

                                    //ContentSMMBenefit
                                    SMMDetail: contentSMMBenefit_fillInSMMDetail ? "Y" : "N",
                                    deductAmt: contentSMMBenefit_deductibleAmount,
                                    reimbursementPercent: contentSMMBenefit_reimbursementPercentage,
                                    overAllLimitType: contentSMMBenefit_overallLimit_type?.value,
                                    overallLimit: contentSMMBenefit_overallLimit,
                                    SMMRemarkOption: contentSMMBenefit_remarks.join(","),
                                    SMMRemark: contentSMMBenefit_remarks_text,
                                });
                                await loadAdmChgPreAuthFormStatus(aid, { value: isChangeToPCA ? "CHGTOPCA" : "APPROVEPCA" });
                                await loadAdmSendEmail(aid);

                                closeModalConfirm();
                                loadAdmGetPreAuthForm(aid).then(() => {
                                    openModalInfo({
                                        title: isChangeToPCA ? "Change to PCA" : "Approve PCA",
                                        isShowSuccessIcon: true,
                                        text: <Flex direction={"column"}>
                                            <FormText>Request is approved and the PCA Form has been sent to : </FormText>
                                            <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span>
                                        </Flex>,
                                        style: { content: { width: 480 } },
                                    });
                                });
                            }
                        },
                        cancelCallback: () => {
                            const { openContentSMMBenefitModal } = get();
                            openContentSMMBenefitModal(aid, value);
                        },
                    });
                } else if (value.value === "APPROVELOG" || value.value === "CHGTOLOG") {
                    const { openContentApprove3Modal } = get();
                    openContentApprove3Modal(aid, value);
                }
            },
            cancelCallback: () => {
                const { contentApprove2_BenefitType } = useAdminApplicationDetailStore.getState();

                if (contentApprove2_BenefitType?.value === "Lum Sum Benefit") {
                    const { openContentApprove2Modal } = get();
                    openContentApprove2Modal(aid, value);
                } else {
                    const { openContentBasicHospitalAndSurgicalBenefitsModal } = get();
                    openContentBasicHospitalAndSurgicalBenefitsModal(aid, value);
                }
            },
        });
    },

    openContentBasicHospitalAndSurgicalBenefitsModal: (aid, value) => {
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        const { setIsValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee, setIsValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee, setIsValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre, setIsValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges, setIsValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit, setIsValid_contentBasicHospitalAndSurgicalBenefits_cnofdays, setIsValid_contentBasicHospitalAndSurgicalBenefits_room_and_board } = get();
        const { openContentSMMBenefitModal, openContentApprove2Modal } = get();

        setIsValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_cnofdays(true);
        setIsValid_contentBasicHospitalAndSurgicalBenefits_room_and_board(true);

        openModalConfirm({
            title: "Basic Hospital and Surgical Benefits",
            modalConfirmPrimaryButtonText: "Next",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentBasicHospitalAndSurgicalBenefits />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: () => {
                const {
                    contentBasicHospitalAndSurgicalBenefits_surgeon_fee,
                    contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee,
                    contentBasicHospitalAndSurgicalBenefits_operating_theatre,
                    contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges,
                    contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit,
                    contentBasicHospitalAndSurgicalBenefits_cnofdays,
                    contentBasicHospitalAndSurgicalBenefits_room_and_board,
                } = useAdminApplicationDetailStore.getState();

                let isValid = true;

                if (contentBasicHospitalAndSurgicalBenefits_surgeon_fee.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee(false);
                    isValid = false;
                }

                if (contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee(false);
                    isValid = false;
                }

                if (contentBasicHospitalAndSurgicalBenefits_operating_theatre.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre(false);
                    isValid = false;
                }

                if (contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges(false);
                    isValid = false;
                }

                if (contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit(false);
                    isValid = false;
                }

                if (contentBasicHospitalAndSurgicalBenefits_cnofdays.length === 0) {
                    setIsValid_contentBasicHospitalAndSurgicalBenefits_cnofdays(false);
                    isValid = false;
                }

                // if (contentBasicHospitalAndSurgicalBenefits_room_and_board.length === 0) {
                //     setIsValid_contentBasicHospitalAndSurgicalBenefits_room_and_board(false);
                //     isValid = false;
                // }

                if (isValid) {
                    closeModalConfirm();
                    openContentSMMBenefitModal(aid, value);
                }
            },
            cancelCallback: () => {
                openContentApprove2Modal(aid, value);
            },
        });
    },

    openContentSUPPLEModal: async (aid, value) => {
        const { openModalConfirm, openModalInfo, closeModalConfirm } = useModalStore.getState();
        const { data, setIsValidEmailTitle, setIsValidEmailTemplate } = get();
        const { openEmailTemplate } = get();

        const loadEmailTemplate = withErrorHandling(openEmailTemplate);

        setIsValidEmailTitle(true);
        setIsValidEmailTemplate(true);

        await loadEmailTemplate(aid, "REQSUP");

        openModalConfirm({
            title: "Compose Email",
            modalConfirmPrimaryButtonText: "Send",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentComposeEmail disabled={true} />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
                const { admSendEmail, admChgPreAuthFormStatus, admGetPreAuthForm } = get();
                const { contentComposeEmail_Title } = useAdminApplicationDetailStore.getState();

                if (!contentComposeEmail_Title) {
                    setIsValidEmailTitle(false);
                } else {
                    const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
                    const loadAdmSendEmail = withErrorHandling(admSendEmail);
                    const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);

                    await loadAdmChgPreAuthFormStatus(aid, value);
                    await loadAdmSendEmail(aid);

                    closeModalConfirm();
                    loadAdmGetPreAuthForm(aid).then(() => {
                        openModalInfo({
                            title: "Compose Email",
                            isShowSuccessIcon: true,
                            text: <FormText>Email has been sent to : <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span></FormText>,
                            style: { content: { width: 480 } },
                        });
                    });
                }
            },
            cancelCallback: () => {

            },
        });
    },

    // 打开拒绝弹窗
    openContentDeclineModal: async (aid, value,updateDateFn=null) => {
        const { setIsValidEmailTitle, setIsValidEmailTemplate, openEmailTemplate, admGetPreAuthForm, resetAllPopupValue } = get();
        const { openModalConfirm, closeModalConfirm, openModalInfo, } = useModalStore.getState();
        const { data } = get();

        const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
        const loadEmailTemplate = withErrorHandling(openEmailTemplate);

        setIsValidEmailTitle(true);
        setIsValidEmailTemplate(true);

        if (data.form.flowType === "LOG") {
            await loadEmailTemplate(aid, "DECLINEDLOG");
        } else if (data.form.flowType === "PCA" || data.form.flowType === "CHGTOPCA") {
            await loadEmailTemplate(aid, "DECLINEDPCA");
        }

        openModalConfirm({
            title: "Decline Request",
            modalConfirmPrimaryButtonText: "Send",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentComposeEmail disabled={true} showReason={true} />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
                const { contentComposeEmail_Template, contentComposeEmail_Title } = useAdminApplicationDetailStore.getState();
                const { admSendEmail, admChgPreAuthFormStatus } = get();

                let isValid = true;

                if (!contentComposeEmail_Title) {
                    setIsValidEmailTitle(false);
                    isValid = false;
                }

                if (!contentComposeEmail_Template?.value) {
                    setIsValidEmailTemplate(false);
                    isValid = false;
                }

                if (isValid) {

                    const loadAdmSendEmail = withErrorHandling(admSendEmail);
                    const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);
                    if(updateDateFn){
                       await updateDateFn() 
                    }
                    await loadAdmChgPreAuthFormStatus(aid, value); 
                    await loadAdmSendEmail(aid);

                    closeModalConfirm();
                    loadAdmGetPreAuthForm(aid).then(() => {
                        resetAllPopupValue();
                        openModalInfo({
                            title: "Decline Request",
                            isShowSuccessIcon: true,
                            text: <FormText>Decline email has been sent to : <span style={{ color: "var(--aia-bx-blue-2)" }}>{data?.user?.email}</span></FormText>,
                            style: { content: { width: 480 } },
                        });
                    });
                }
            },
            cancelCallback: () => {
                // openContentApprove1Modal(value);
            },
        });
    },
        // 打开拒绝弹窗第一版
    openContentDeclineFirstModal: async (aid, value) => { 
        const { openModalConfirm, closeModalConfirm } = useModalStore.getState();
        openModalConfirm({
          title: "Decline Request",
          modalConfirmPrimaryButtonText: "Save",
          modalConfirmSecondaryButtonText: "Back",
          content: <ContentApprove1 />,
          style: { content: { width: 720 } },
          callback: async () => {
            const { contentApprove1_TreatmentType, contentApprove1_ClassificationOfSurgery, contentApprove1_ICD, contentApprove1_ICD_code, contentApprove1_Remarks, contentApprove1_Remarks_text, contentApprove1_savingAmount } = useAdminApplicationDetailStore.getState();
            const { data, admUpdatePreAuthClaim, admChgPreAuthFormStatus, admGetPreAuthForm } = get();
            const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
            const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
            
            async function updateDate(){
                await loadAdmUpdatePreAuthClaim(aid, {
                    treatmentType: contentApprove1_TreatmentType?.value ?? "",
                    classificationSurgery: contentApprove1_ClassificationOfSurgery?.value ?? "",
                    ICDCode: contentApprove1_ICD ?? "",
                    ICDCodeList: contentApprove1_ICD_code.join(","),
                    treatmentRemarkOption: contentApprove1_Remarks?.join(","),
                    treatmentRemark: contentApprove1_Remarks_text ?? "",
                    saveAmt: contentApprove1_savingAmount ?? "",
                });
            }
      
            const { openContentDeclineModal } = get();
            
            openContentDeclineModal(aid,value,updateDate)
          },
          cancelCallback: () => {
            closeModalConfirm()
          },
        });
    },   

    openContentClaimRequest: async (aid, value) => {
        const { openModalConfirm, closeModalConfirm, openModalInfo } = useModalStore.getState();

        openModalConfirm({
            title: "Claim Request",
            modalConfirmPrimaryButtonStyleChangeToDanger: true,
            modalConfirmPrimaryButtonText: "Confirm Claim",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentClaimRequest />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
                const { contentClaimRequest_Incurred_amount, contentClaimRequest_paid_amount, contentClaimRequest_chargeback, contentClaimRequest_wis_no, contentClaimRequest_debit_no } = useAdminApplicationDetailStore.getState();
                const { setIsValid_contentClaimRequest_Incurred_amount, setIsValid_contentClaimRequest_paid_amount, setIsValid_contentClaimRequest_chargeback, setIsValid_contentClaimRequest_wis_no, setIsValid_contentClaimRequest_debit_no } = useAdminApplicationDetailStore.getState();
                const { admChgPreAuthFormStatus, admUpdatePreAuthClaim, admGetPreAuthForm } = get();

                const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
                const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
                const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);

                setIsValid_contentClaimRequest_Incurred_amount(true);
                setIsValid_contentClaimRequest_paid_amount(true);
                setIsValid_contentClaimRequest_chargeback(true);
                setIsValid_contentClaimRequest_wis_no(true);
                setIsValid_contentClaimRequest_debit_no(true);

                let isValid = true;

                if (!contentClaimRequest_Incurred_amount) {
                    setIsValid_contentClaimRequest_Incurred_amount(false);
                    isValid = false;
                }

                if (!contentClaimRequest_paid_amount) {
                    setIsValid_contentClaimRequest_paid_amount(false);
                    isValid = false;
                }

                if (!contentClaimRequest_chargeback) {
                    setIsValid_contentClaimRequest_chargeback(false);
                    isValid = false;
                }

                if (!contentClaimRequest_wis_no) {
                    setIsValid_contentClaimRequest_wis_no(false);
                    isValid = false;
                }

                if (!contentClaimRequest_debit_no) {
                    setIsValid_contentClaimRequest_debit_no(false);
                    isValid = false;
                }


                if (isValid) {

                    await loadAdmUpdatePreAuthClaim(aid, {
                        //ContentClaimRequest
                        incurredAmt: contentClaimRequest_Incurred_amount,
                        paidAmt: contentClaimRequest_paid_amount,
                        chargeBack: contentClaimRequest_chargeback,
                        WISNo: contentClaimRequest_wis_no,
                        debitno: contentClaimRequest_debit_no
                    });
                    await loadAdmChgPreAuthFormStatus(aid, value);

                    closeModalConfirm();
                    loadAdmGetPreAuthForm(aid).then(() => {
                        openModalInfo({
                            title: "Claim Request",
                            isShowSuccessIcon: true,
                            text: <FormText>Claim is confirmed.</FormText>,
                            style: { content: { width: 480 } },
                        });
                    });
                }
            },
            cancelCallback: () => {

            },
        });
    },

    openContentScheduledChargeback: async (aid, value) => {
        const { openModalConfirm, closeModalConfirm, openModalInfo } = useModalStore.getState();

        openModalConfirm({
            title: "Chargeback",
            modalConfirmPrimaryButtonStyleChangeToDanger: true,
            modalConfirmPrimaryButtonText: "Charge",
            modalConfirmSecondaryButtonText: "Back",
            content: <ContentScheduledChargeback />,
            closeManually: true,
            style: { content: { width: 720 } },
            callback: async () => {
                const { contentScheduledChargeback_Chargeback_datatime, setIsValid_contentScheduledChargeback_Chargeback_datatime } = useAdminApplicationDetailStore.getState();
                const { admChgPreAuthFormStatus, admUpdatePreAuthClaim, admGetPreAuthForm } = get();

                const loadAdmGetPreAuthForm = withErrorHandling(admGetPreAuthForm);
                const loadAdmUpdatePreAuthClaim = withErrorHandling(admUpdatePreAuthClaim);
                const loadAdmChgPreAuthFormStatus = withErrorHandling(admChgPreAuthFormStatus);

                setIsValid_contentScheduledChargeback_Chargeback_datatime(true);

                let isValid = true;

                if (!contentScheduledChargeback_Chargeback_datatime) {
                    setIsValid_contentScheduledChargeback_Chargeback_datatime(false);
                    isValid = false;
                }

                if (isValid) {
                    await loadAdmUpdatePreAuthClaim(aid, {
                        //ContentScheduledChargeback
                        chargeBackDt: dayjs(contentScheduledChargeback_Chargeback_datatime).format('YYYY-MM-DD'),
                        chargeBackTime: dayjs(contentScheduledChargeback_Chargeback_datatime).format('HH:mm'),
                    });
                    await loadAdmChgPreAuthFormStatus(aid, value);

                    closeModalConfirm();
                    loadAdmGetPreAuthForm(aid).then(() => {
                        openModalInfo({
                            title: "Claim Request",
                            isShowSuccessIcon: true,
                            text: <FormText>The chargeback is completed and claim settlement request was sent to finance.</FormText>,
                            style: { content: { width: 480 } },
                        });
                    });
                }
            },
            cancelCallback: () => {

            },
        });
    },

    resetAllPopupValue: (value) => set((state) => {
        return {
            //ContentApprove1
            contentApprove1_TreatmentType: {
                "value": "",
                "label": "Please Select"
            },
            contentApprove1_ClassificationOfSurgery: {
                "value": "",
                "label": "Please Select"
            },
            contentApprove1_ICD: "",
            contentApprove1_ICD_code: [],
            contentApprove1_ICD_code_list: [],
            contentApprove1_Remarks: [],
            contentApprove1_Remarks_text: "",
            contentApprove1_savingAmount: "",

            //ContentApprove2
            contentApprove2_Operation: {
                "value": "",
                "label": "Please Select",
            },
            isValid_contentApprove2_Operation: true,
            contentApprove2_Accommodation: {
                "value": "",
                "label": "Please Select",
            },
            isValid_contentApprove2_Accommodation: true,
            contentApprove2_BenefitType: {
                value: "",
                label: "Please Select",
            },
            isValid_contentApprove2_BenefitType: true,
            contentApprove2_lumpSumBenefitAmount: "",
            isValid_contentApprove2_lumpSumBenefitAmount: true,

            //ContentApprove3
            contentApprove3_roomClass_option1: "",
            contentApprove3_roomClass_option2: "",
            contentApprove3_roomClass_option3: "",
            isValid_contentApprove3_roomClass_option: true,
            contentApprove3_logAmount: "",
            isValid_contentApprove3_logAmount: true,

            //ContentBasicHospitalAndSurgicalBenefits
            contentBasicHospitalAndSurgicalBenefits_surgeon_fee: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_surgeon_fee: true,
            contentBasicHospitalAndSurgicalBenefits_surgeon_fee_remark: "",

            contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee: true,
            contentBasicHospitalAndSurgicalBenefits_anaesthetist_fee_remark: "",

            contentBasicHospitalAndSurgicalBenefits_operating_theatre: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_operating_theatre: true,
            contentBasicHospitalAndSurgicalBenefits_operating_theatre_remark: "",

            contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges: true,
            contentBasicHospitalAndSurgicalBenefits_miscellaneous_hospital_charges_remark: "",

            contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit: true,
            contentBasicHospitalAndSurgicalBenefits_physician_hospital_visit_remark: "",

            contentBasicHospitalAndSurgicalBenefits_cnofdays: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_cnofdays: true,

            contentBasicHospitalAndSurgicalBenefits_room_and_board: "",
            isValid_contentBasicHospitalAndSurgicalBenefits_room_and_board: true,
            contentBasicHospitalAndSurgicalBenefits_room_and_board_remark: "",

            // alex 叫改成这个初始值。  
            contentSMMBenefit_overallLimit_type: {
                "label": "Please Select",
                "value": "",
                "color": "black"
            },

            //ContentSMMBenefit
            contentSMMBenefit_fillInSMMDetail: false,
            contentSMMBenefit_deductibleAmount: "",
            contentSMMBenefit_reimbursementPercentage: "",
            contentSMMBenefit_overallLimit: "",
            contentSMMBenefit_remarks: [],
            contentSMMBenefit_remarks_text: "",

            //ContentComposeEmail
            // contentComposeEmail_Template: {
            //     "label": "Please Select Email Template",
            //     "value": ""
            // },
            // isValidEmailTemplate: true,
            //contentComposeEmail_Reason: [],
            //contentComposeEmail_Title: "",
            // isValidEmailTitle: true,
            // contentComposeEmail_Content: "",

            //ContentClaimRequest
            contentClaimRequest_Incurred_amount: "",
            contentClaimRequest_paid_amount: "",
            contentClaimRequest_chargeback: "",
            contentClaimRequest_wis_no: "",
            isValid_contentClaimRequest_Incurred_amount: true,
            isValid_contentClaimRequest_paid_amount: true,
            isValid_contentClaimRequest_chargeback: true,
            isValid_contentClaimRequest_wis_no: true,

            //ContentScheduledChargeback
            contentScheduledChargeback_Chargeback_datatime: "",
            isValid_contentScheduledChargeback_Chargeback_datatime: true,
        }
    }),

    detailRedPoint: "0",
    setDetailRedPoint: (value) => set({ detailRedPoint: value }),

    documentRedPoint: "0",
    setDocumentRedPoint: (value) => set({ documentRedPoint: value }),

    suppattachmentForRedPoint: [],
    setSuppattachmentForRedPoint: (value) => set({ suppattachmentForRedPoint: value }),

    admDeleteAttachment: async (id) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/general/delete/${id}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        // const data = await response.json();

    },

    admNotedPreAuthForm: async (id) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admNotedPreAuthForm?aid=${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        // const data = await response.json();

    },

    admGeneratePDF: async (type, aid) => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admGeneratePDF`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                "aid": aid,
                "templateName": type
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        return data.url;
    },

    admGetICDMaintenanceList: async (keyword = "") => {
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
        const { setContentApprove1_ICD_code_list } = get();

        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/admGetICDMaintenanceList?search=${keyword}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response.json();

        setContentApprove1_ICD_code_list(data?.ICDList?.map((item) => {
            return {
                label: `${item.ICDCode} - ${item.ICDDescription}`,
                value: item.ICDCode,
            }
        }));
    },

    showSTPWarningModal: (callback) => {
        const { data } = useAdminApplicationDetailStore.getState();
        const { openModalConfirm } = useModalStore.getState();

        if (data?.form?.flowType === "STP") {
            openModalConfirm({
                title: "STP Request",
                text: <FormText>Your edit will change the request status to the normal manual PCA status. Are you sure you want to proceed with edit?</FormText>,
                style: { content: { width: 480 } },
                modalConfirmPrimaryButtonText: "Yes",
                modalConfirmSecondaryButtonText: "No",
                callback: () => {
                    callback();
                }
            });
        } else {
            callback();
        }
    }, 
    
    //是否允许release limit
    isAllowManualReleaseLimit:async (aid)=>{
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState();
 
        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/isAllowManualReleaseLimit?aid=${aid}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
            throw new Error('Unauthorized');
        }

        if (!response.ok) {
            setLoading(false);
            throw new Error('Network Error');
        }

        const data = await response?.json()
        return data?.data; 
    },

    //点击按钮发送  release limit请求
    ManualReleaseLimit:async (aid,releaseCardLimitDate)=>{
        const { token, logout } = useAuthAdminStore.getState();
        const { setLoading } = useLoadingStore.getState(); 
        setLoading(true)
        const response = await fetch(`${apiDomain}/${basename}/api/preAuthAdmin/ManualReleaseLimit`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body:JSON.stringify({
                "aid": aid,
                "releaseCardLimitDate": releaseCardLimitDate
            }),
        });

        if (response.status === 401) { // Unauthorized
            logout();
            setLoading(false);
        }

        if (!response.ok) {
            setLoading(false);
        }
        setLoading(false);
        const data = await response?.json()
        return data; 
    },
    requestReleaseLimit: {
        requestReleaseCardCreateDate:null,
        requestReleaseCardUpdateDate:null,
        actullyReleaseCardLimitDate:null, //實際release limit的時間
        releaseCardLimitDate:null  //申請執行release limit的時間
    },
    setRequestReleaseLimit: (value) => set((state) => {
        return { requestReleaseLimit: value }
    }),
}))

export default useAdminApplicationDetailStore;